import type { EventWithSensors } from "@sitescene/types";

export interface MergeWebsocketEventOptions {
	/** Only `status_changed` websocket events may overwrite cached status. */
	applyStatusFromIncoming: boolean;
	preferIncomingMeasurements: boolean;
	preferIncomingSensors: boolean;
}

/**
 * Merge a PATCH response into a cached detail entry, preserving populated
 * relational fields while taking authoritative scalar fields (including
 * `status`, `updatedAt`, and `lastEditedBy`) from the server response.
 */
export function mergeMutationResponseIntoCachedEvent(
	cached: EventWithSensors,
	incoming: EventWithSensors,
): EventWithSensors {
	return {
		...cached,
		...incoming,
		installation: cached.installation,
		measurements: cached.measurements,
		mediaKeys: cached.mediaKeys,
		preliminary: incoming.preliminary ?? cached.preliminary,
		sensors: cached.sensors,
		thumbnailKeys: cached.thumbnailKeys,
		zones: cached.zones,
	};
}

/**
 * Merge a websocket payload into a cached entry. The converter fabricates
 * placeholder timestamps and may carry ref-only relational fields, so
 * timestamps and populated relations follow websocket-specific rules.
 */
export function mergeWebsocketEventIntoCachedEvent(
	cached: EventWithSensors,
	incoming: EventWithSensors,
	options: MergeWebsocketEventOptions,
): EventWithSensors {
	return {
		...cached,
		...incoming,
		createdAt: cached.createdAt,
		installation: cached.installation,
		measurements: options.preferIncomingMeasurements
			? incoming.measurements
			: cached.measurements,
		mediaKeys: cached.mediaKeys,
		preliminary: incoming.preliminary ?? cached.preliminary,
		sensors: options.preferIncomingSensors ? incoming.sensors : cached.sensors,
		status: options.applyStatusFromIncoming ? incoming.status : cached.status,
		thumbnailKeys: cached.thumbnailKeys,
		updatedAt: cached.updatedAt,
		zones: cached.zones,
	};
}
